import React from "react";
import { useFormikContext } from "formik";
import { ChildDataType, DeclarationOfTruth } from "csv-package";

import Button from "../../../components/Button";
import ButtonContainer from "../../../components/ButtonContainer";
import PageTitle from "../../../components/PageTitle";
import QuestionsFrame from "../../../components/QuestionsFrame";
import ReDeclareSection from "../../../components/ReDeclareSection";

import { FlatApplciationType } from "../../../types";
import useReDeclare, { ReDeclareModals } from "../../../hooks/useReDeclare";
import ReDeclareNarrative from "../../../components/ReDeclareNarrative";
import ReDeclareSectionChild from "../../../components/ReDeclareSectionChild";
import _ from "lodash";

const ReDeclare: React.FC = () => {
  const { values } = useFormikContext<FlatApplciationType>();

  const {
    handleAcceptReDeclare,
    handleCallBack,
    reDeclareObject,
    reDeclareState,
    handleDeclarationOfTruth,
    handleErrorModalClose,
  } = useReDeclare();

  if (reDeclareObject.Status === "canceled") {
    return null;
  }

  const reDeclareCreateDate = new Date(reDeclareObject.Created);
  return (
    <>
      <PageTitle
        heading="Application amendments"
        description={`The following sections have been amended by the Magistrates’ Court based on your telephone conversation that occurred on the ${reDeclareCreateDate.toLocaleDateString(
          "en-AU"
        )}. Please review these amendments to ensure they’re accurate before re-submitting.`}
      />

      <ReDeclareSection
        title="Your role"
        names={[
          "seeking",
          "seeking_appl_role"
        ]}
        reDeclareObject={reDeclareObject}
        values={values}
      />

      <ReDeclareNarrative
        title="Your statement"
        reDeclareObject={reDeclareObject}
        values={values}
      />

      <ReDeclareSection
        title="Applicant details"
        names={[
          "appl_det_giv_nam",
          "appl_det_fam_nam",
          "appl_det_dob",
          "appl_det_sex",
          "appl_det_sex_other",
        ]}
        reDeclareObject={reDeclareObject}
        values={values}
      />

      <ReDeclareSection
        title="Applicant address"
        names={[
          "appl_det_addr_flat",
          "appl_det_addr_strnr",
          "appl_det_addr_strname",
          "appl_det_addr_strtype",
          "appl_det_addr_sub",
          "appl_det_addr_state",
          "appl_det_addr_postcode",
          "appl_det_addr_secret",
          "appl_det_court_heard",
          "CourtID"
        ]}
        reDeclareObject={reDeclareObject}
        values={values}
      />

      <ReDeclareSection
        title="Court hearing"
        names={[
          "appl_det_court_in_person",
        ]}
        reDeclareObject={reDeclareObject}
        values={values}
      />

      <ReDeclareSection
        title="Contact details"
        names={[
          "appl_safe_cont",
          "appl_cont_phone",
          "appl_cont_phone_2",
          "appl_cont_phone_3",
          "appl_cont_phone_vm",
          "appl_cont_safe_times",
          "appl_cont_email",
        ]}
        reDeclareObject={reDeclareObject}
        values={values}
      />

      <ReDeclareSection
        title="Original order"
        names={[
          "eo_case_no",
          "eo_aprox_date",
          "eo_expiry"
      ]}
        reDeclareObject={reDeclareObject}
        values={values}
      />

      <ReDeclareSection
        title="Protected persons"
        names={["prp_person"]}
        reDeclareObject={reDeclareObject}
        values={values}
      />

      {Array.isArray(reDeclareObject.Data.protected_persons) &&
        (reDeclareObject.Data.protected_persons as ChildDataType[])
          .sort((a, b) => {
            if (typeof a.index === "number" && typeof b.index === "number") {
              return a.index - b.index;
            }
            return 0;
          })
          .map((data, index) => (
            <ReDeclareSectionChild
              key={index}
              index={index}
              values={values}
              titlePrefix="Protected person "
              data={data}
              names={[
                "prp_giv_nam",
                "prp_fam_nam",
                "prp_dob2",
                "prp_sex",
                "prp_sex_other",
                "seeking_appl_role",
                "seeking_appl_sub_role",
              ]}
            />
        ))}

      <ReDeclareSection
        title="Protected persons - Court Applications for Children"
        names={["prp_ord_fc"]}
        reDeclareObject={reDeclareObject}
        values={values}
      />

      <ReDeclareSection
        title="Respondent"
        names={[
          "resp_det_giv_nam",
          "resp_det_fam_nam",
          "resp_det_other_names",
          "resp_addr_known",
          "resp_addr_known_pc",
          "resp_addr_known_pc_stn",
          "resp_addr_known_pc_ofc",
          "resp_det_addr_flat",
          "resp_det_addr_strnr",
          "resp_det_addr_strname",
          "resp_det_addr_strtype",
          "resp_det_addr_sub",
          "resp_det_addr_state",
          "resp_det_addr_postcode",
          "resp_cont",
          "resp_cont_phone",
          "resp_cont_phone_2",
          "resp_cont_phone_3",
          "resp_cont_email",
          "resp_cont_other",
          "resp_weapon_access",
        ]}
        reDeclareObject={reDeclareObject}
        values={values}
      />

      <ReDeclareSection
        title="Application type"
        names={[
          "app_types",
        ]}
        reDeclareObject={reDeclareObject}
        values={values}
      />
        
      <ReDeclareSection
        title="Application details"
        names={[
          "app_type_extend_further_violence_check",
          "app_type_extend_further_violence",
          "incident_pys",
          "incident_thy",
          "incident_cdw",
          "incident_fsw_detail",
          "app_type_extend_why_breached",
          "app_type_extend_why_violence_stopped",
          "app_type_extend_still_fearful",
          "appl_pregnant_baby",
          "app_type_extend_duration",
          "vary_resp_children",
          "revoke_appl_why",
        ]}
        reDeclareObject={reDeclareObject}
        values={values}
      />

      {Array.isArray(reDeclareObject.Data.children) &&
        reDeclareObject.Data.children
          .sort((a, b) => {
            if (typeof a.index === "number" && typeof b.index === "number") {
              return a.index - b.index;
            }
            return 0;
          })
          .map((data, index) => (
            <ReDeclareSectionChild
              key={index}
              index={index}
              values={values}
              titlePrefix="Child "
              data={data}
              names={[
                "child_action",
                "prp_giv_nam",
                "prp_giv_nam2",
                "prp_fam_nam",
                "prp_dob",
                "prp_live",
                "prp_addr_flat",
                "prp_addr_strnr",
                "prp_addr_strname",
                "prp_addr_strtype",
                "prp_addr_sub",
                "prp_addr_state",
                "prp_addr_postcode",
                "prp_rel_to_appl",
                "prp_rel_to_appl_other",
                "prp_rel_to_resp",
                "prp_rel_to_resp_other",
                "prp_sex1",
                "prp_sex_other",
                "prp_abor_code",
                "prp_disabl",
                "prp_disabl_req_other",
                "prp_ord_exist",
              ]}
            />
          ))}

      <ReDeclareSection
        title="Information sharing"
        names={[
          "appl_info_share",
          {
            name: "appl_info_share_limited",
            label: "Please deselect the information sharing entities you consent to",
          },
        ]}
        reDeclareObject={reDeclareObject}
        values={values}
      />

      <QuestionsFrame>
        <DeclarationOfTruth
          applicantName={`${_.get(
            reDeclareObject,
            "Data.appl_det_giv_nam",
            values.appl_det_giv_nam
          )}  ${_.get(
            reDeclareObject,
            "Data.appl_det_fam_nam",
            values.appl_det_fam_nam
          )}`}
          applicantDOB={
            _.get(reDeclareObject, "Data.appl_det_dob", values.appl_det_dob) +
            ""
          }
          checked={reDeclareState.declaration_of_truth}
          onCheckboxChange={handleDeclarationOfTruth}
        />
      </QuestionsFrame>
      <ButtonContainer>
        <Button
          variant="secondary"
          action={handleCallBack}
        >
          {
            {
              uninitialised: "Reject and request a call",
              loading: "Requesting...",
              successful: "Request successful",
              failed: "Request failed",
            }[reDeclareState.call_back_status]
          }
        </Button>
        <Button
          action={handleAcceptReDeclare}
        >
          {
            {
              uninitialised: "Accept changes and redeclare",
              loading: "Submitting...",
              successful: "Request successful",
              failed: "Request failed",
            }[reDeclareState.submit_status]
          }
        </Button>
      </ButtonContainer>

      <ReDeclareModals
        reDeclareState={reDeclareState}
        handleErrorModalClose={handleErrorModalClose}
      />
    </>
  );
};

export default ReDeclare;
